import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
	Box,
	Title,
	Text,
	Button,
	Table,
	Modal,
	TextInput,
	Group,
} from '@mantine/core'
import { getOrgs, createOrg } from '../services/actions/controlActions'
import { useDispatch } from 'react-redux'
import { switchOrg } from '../services/actions/userActions'
import { loadUserContext } from '../services/utils/loadUserContext'

const ORG_FIELD_MAX_LENGTH = 55

const ControlOrgsPage = () => {
	const navigate = useNavigate()
	const dispatch = useDispatch()

	const [orgs, setOrgs] = useState([])
	const [error, setError] = useState('')
	const [showModal, setShowModal] = useState(false)
	const [newOrg, setNewOrg] = useState({ name: '', slug: '' })

	const generateSlug = (value) =>
		value
			.toLowerCase()
			.replace(/[^a-z0-9\s-]/g, '')
			.trim()
			.replace(/\s+/g, '-')
			.replace(/^-+/, '')

	useEffect(() => {
		const loadOrgs = async () => {
			try {
				const data = await getOrgs()
				setOrgs(data)
			} catch (err) {
				console.error('Failed to load orgs:', err)
				setError('Failed to load organizations.')
			}
		}

		loadOrgs()
	}, [])

	const handleCreateOrg = async () => {
		const normalizedName = newOrg.name.trim()
		const normalizedSlug = generateSlug(newOrg.slug)

		if (!normalizedName) {
			setError('Organization name cannot be empty.')
			return
		}
		if (!normalizedSlug) {
			setError('Slug cannot be empty.')
			return
		}
		if (normalizedName.length > ORG_FIELD_MAX_LENGTH) {
			setError(
				`Organization name must be ${ORG_FIELD_MAX_LENGTH} characters or fewer.`
			)
			return
		}
		if (normalizedSlug.length > ORG_FIELD_MAX_LENGTH) {
			setError(`Slug must be ${ORG_FIELD_MAX_LENGTH} characters or fewer.`)
			return
		}

		try {
			const created = await createOrg({
				...newOrg,
				name: normalizedName,
				slug: normalizedSlug,
			})

			setShowModal(false)
			setNewOrg({ name: '', slug: '' })

			const updated = await getOrgs()
			setOrgs(updated)

			await switchOrg(created.slug)
			await loadUserContext(dispatch)
			navigate(`/control/orgs/${created.slug}/details`)
		} catch (err) {
			console.error('Failed to create org:', err)
			setError('Failed to create organization.')
		}
	}

	const handleSwitchAndNavigate = async (org) => {
		try {
			await switchOrg(org)
			await loadUserContext(dispatch)
			navigate(`/control/orgs/${org}/details`)
		} catch (err) {
			console.error('❌ Failed to switch org and load context:', err)
			setError('Failed to switch organization.')
		}
	}

	return (
		<Box px='md' py='md'>
			<Title order={2} mb='md'>
				Organizations
			</Title>

			{error && (
				<Text color='red' mb='md'>
					{error}
				</Text>
			)}

			<Button mb='lg' onClick={() => setShowModal(true)}>
				➕ Create New Organization
			</Button>

			{orgs.length > 0 ? (
				<Table withBorder striped highlightOnHover>
					<thead>
						<tr>
							<th>Name</th>
							<th>Slug</th>
							<th>Plan</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						{orgs.map((org) => (
							<tr key={org.id}>
								<td>{org.name}</td>
								<td>{org.slug}</td>
								<td>{org?.plan?.name || 'Unassigned'}</td>
								<td style={{ textAlign: 'right' }}>
									<Group spacing='xs' position='right'>
										<Button
											size='xs'
											onClick={() => handleSwitchAndNavigate(org.slug)}
										>
											Details
										</Button>
										<Button
											size='xs'
											variant='outline'
											onClick={async () => {
												try {
													await switchOrg(org.slug)
													await loadUserContext(dispatch)
													navigate(`/control/orgs/${org.slug}/billing`)
												} catch (err) {
													console.error(
														'❌ Failed to switch org and open billing:',
														err,
													)
													setError('Failed to switch organization.')
												}
											}}
										>
											Billing
										</Button>
									</Group>
								</td>
							</tr>
						))}
					</tbody>
				</Table>
			) : (
				<Text>No organizations found.</Text>
			)}

			<Modal
				opened={showModal}
				onClose={() => setShowModal(false)}
				title='Create New Organization'
				centered
			>
				<form
					onSubmit={(e) => {
						e.preventDefault()
						handleCreateOrg()
					}}
				>
					<TextInput
						label='Organization Name'
						value={newOrg.name}
						onChange={(e) => {
							const name = e.target.value
								.replace(/^\s+/, '')
								.slice(0, ORG_FIELD_MAX_LENGTH)
							setNewOrg({
								name,
								slug: generateSlug(name),
							})
						}}
						onBlur={(e) => {
							const name = e.target.value.trim()
							setNewOrg({
								name,
								slug: generateSlug(name),
							})
						}}
						maxLength={ORG_FIELD_MAX_LENGTH}
						required
						mb='sm'
					/>

					<TextInput
						label='Slug'
						value={newOrg.slug}
						onChange={(e) =>
							setNewOrg({
								...newOrg,
								slug: generateSlug(e.target.value).slice(
									0,
									ORG_FIELD_MAX_LENGTH
								),
							})
						}
						maxLength={ORG_FIELD_MAX_LENGTH}
						required
					/>

					<Group mt='lg' position='right'>
						<Button variant='light' onClick={() => setShowModal(false)}>
							Cancel
						</Button>
						<Button type='submit'>Save</Button>
					</Group>
				</form>
			</Modal>
		</Box>
	)
}

export default ControlOrgsPage
